import { create } from 'zustand';
import { useEffect } from 'react';
import { Character, type BuffDTO } from '@/network/RestApi';
import { useUIStore } from '@/state/uiStore';

export interface ActiveBuff extends BuffDTO {
  fetchedAt: number;
}

interface BuffState {
  buffs: ActiveBuff[];
  setBuffs: (b: ActiveBuff[]) => void;
  clear: () => void;
}

export const useBuffStore = create<BuffState>((set) => ({
  buffs: [],
  setBuffs: (b) => set({ buffs: b }),
  clear: () => set({ buffs: [] }),
}));

/**
 * Compute total multiplier of all active (non-expired) buffs of a kind.
 * Multiple buffs of the same kind multiply together (e.g. ×1.5 × ×1.2 = ×1.8).
 * Returns 1 if no active buff of that kind.
 */
export function getActiveMultiplier(buffs: ActiveBuff[], kind: string, nowMs: number = Date.now()): number {
  let mul = 1;
  for (const b of buffs) {
    if (b.kind !== kind) continue;
    const remaining = b.remainingS - Math.floor((nowMs - b.fetchedAt) / 1000);
    if (remaining > 0) mul *= b.multiplier;
  }
  return mul;
}

/**
 * Polls /api/character/:id/buffs every 30s and on refresh ticks.
 * Mount once at the HUD root so all consumers share one source of truth.
 */
export function useBuffPolling(charId: number | null | undefined) {
  const refreshTick = useUIStore((s) => s.buffRefreshTick);
  useEffect(() => {
    if (!charId) {
      useBuffStore.getState().clear();
      return;
    }
    let cancelled = false;
    const load = () => {
      Character.buffs(charId)
        .then((r) => {
          if (cancelled) return;
          const now = Date.now();
          useBuffStore.getState().setBuffs((r.buffs ?? []).map((b) => ({ ...b, fetchedAt: now })));
        })
        .catch(() => { if (!cancelled) useBuffStore.getState().clear(); });
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [charId, refreshTick]);
}

